import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import ClientLayout from "../components/ClientLayout";
import SafeHtml from "../components/SafeHtml";
import { FileText, Download, ArrowRight, BookOpen, Clock, Calendar, AlertCircle, Scroll, CalendarClock } from "lucide-react";

export default function ClientHome() {
  const { token, nameSlug = "client" } = useParams();
  const base = `/${nameSlug}/${token}`;
  const [data, setData] = useState(null);
  const [guidelines, setGuidelines] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/client/${token}`).then(({ data }) => setData(data)).catch((e) => setError(e?.response?.status === 404 ? "invalid" : "error"));
    api.get(`/client/${token}/guidelines`).then(({ data }) => setGuidelines(data)).catch(() => {});
  }, [token]);

  if (error === "invalid") {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-[hsl(var(--brand-cream))]" data-testid="client-invalid-link">
        <div className="text-center max-w-md">
          <div className="font-display text-4xl">This link is no longer active.</div>
          <p className="text-[hsl(var(--brand-muted))] mt-3">Please reach out to Amisha on WhatsApp for a fresh link.</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen grid place-items-center text-[hsl(var(--brand-muted))] font-serif-italic">Opening your plan…</div>;

  const { client, active_plan, plans, dietician, payment_notice, next_follow_up } = data;
  const past = plans.filter((p) => !active_plan || p.id !== active_plan.id);
  const downloadUrl = active_plan ? `${process.env.REACT_APP_BACKEND_URL}/api/client/${token}/plans/${active_plan.id}/pdf` : null;
  const waLink = dietician?.phone ? `https://wa.me/${dietician.phone.replace(/[^0-9]/g, "")}` : "#";

  // Pre-filled renewal message — only used when the payment notice is showing
  const renewMessage = payment_notice
    ? (payment_notice.status === "expired"
        ? `Hi Amisha, my plan expired on ${payment_notice.paid_until}. I'd like to renew. — ${client.name}`
        : `Hi Amisha, my plan ends on ${payment_notice.paid_until}. I'd like to renew. — ${client.name}`)
    : "";
  const renewWaLink = dietician?.phone && renewMessage
    ? `https://wa.me/${dietician.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(renewMessage)}`
    : waLink;

  // Format next follow-up date nicely
  let followUpLine = null;
  if (next_follow_up?.date) {
    try {
      const d = new Date(next_follow_up.date + "T00:00:00");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffDays = Math.round((d - today) / 86400000);
      const formatted = d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
      if (diffDays === 0) followUpLine = `Today · ${formatted}`;
      else if (diffDays === 1) followUpLine = `Tomorrow · ${formatted}`;
      else if (diffDays > 0 && diffDays < 7) followUpLine = `In ${diffDays} days · ${formatted}`;
      else followUpLine = formatted;
      if (next_follow_up.time) followUpLine += ` at ${next_follow_up.time}`;
    } catch (_) {}
  }

  return (
    <ClientLayout dietician={dietician} clientName={client.name}>
      {payment_notice ? (
        <div className={`border-b ${payment_notice.status === "expired" ? "bg-red-50 border-red-200" : "bg-[hsl(var(--brand-terracotta))]/10 border-[hsl(var(--brand-terracotta))]/30"}`} data-testid="payment-notice">
          <div className="max-w-5xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between gap-4 flex-wrap text-sm">
            <div className="flex items-center gap-2">
              <AlertCircle className={`w-4 h-4 ${payment_notice.status === "expired" ? "text-red-600" : "text-[hsl(var(--brand-terracotta))]"}`} />
              {payment_notice.status === "expired"
                ? <span>Your package expired on <b>{payment_notice.paid_until}</b>. Tap below to renew with Amisha.</span>
                : <span>Your package ends on <b>{payment_notice.paid_until}</b> ({payment_notice.days} day{payment_notice.days === 1 ? "" : "s"} left).</span>
              }
            </div>
            <a
              href={renewWaLink}
              target="_blank"
              rel="noreferrer"
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-xs font-medium shadow-sm transition-colors ${payment_notice.status === "expired" ? "bg-red-600 hover:bg-red-700" : "bg-[hsl(var(--brand-sage))] hover:bg-[hsl(var(--brand-sage-hover))]"}`}
              data-testid="renew-wa-link"
            >
              {payment_notice.status === "expired" ? "Renew now on WhatsApp" : "Renew on WhatsApp"}
            </a>
          </div>
        </div>
      ) : null}

      <section className="max-w-5xl mx-auto px-5 md:px-8 pt-14 md:pt-20">
        <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))]">Welcome</div>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] mt-3">
          Hello, <span className="font-serif-italic text-[hsl(var(--brand-sage))]">{client.name.split(" ")[0]}.</span>
        </h1>
        <p className="mt-5 text-[hsl(var(--brand-muted))] max-w-2xl text-lg leading-relaxed">
          Your space for the weekly plan Amisha has prepared — along with recipes for each meal. Take your time.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-14 md:mt-20">
        {followUpLine ? (
          <div className="mb-6 flex items-center gap-3 bg-[hsl(var(--brand-sage))]/10 border border-[hsl(var(--brand-sage))]/30 rounded-xl px-5 py-4" data-testid="client-next-followup">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--brand-sage))]/20 grid place-items-center text-[hsl(var(--brand-sage))]">
              <CalendarClock className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[hsl(var(--brand-muted))]">Next follow-up</div>
              <div className="font-display text-xl text-[hsl(var(--brand-ink))]">{followUpLine}</div>
            </div>
          </div>
        ) : null}
        {active_plan ? (
          <div className="bg-white border border-[hsl(var(--brand-border))] rounded-2xl overflow-hidden" data-testid="active-plan-card">
            <div className="grid md:grid-cols-[1fr_1fr] gap-0">
              <div className="p-8 md:p-12">
                <div className="flex items-center gap-2 text-[hsl(var(--brand-sage))]">
                  <Calendar className="w-4 h-4" />
                  <div className="text-xs tracking-[0.3em] uppercase">Your current week</div>
                </div>
                <h2 className="font-display text-4xl mt-3 leading-tight">{active_plan.title}</h2>
                <div className="mt-3 text-[hsl(var(--brand-muted))]">
                  {active_plan.start_date} — {active_plan.end_date}
                </div>
                {active_plan.notes ? (
                  <p className="mt-5 text-[hsl(var(--brand-muted))] font-serif-italic">"{active_plan.notes}"</p>
                ) : null}
                <div className="mt-8 flex flex-wrap gap-2">
                  <Link to={`${base}/plan/${active_plan.id}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))]" data-testid="view-active-plan-btn">
                    <FileText className="w-4 h-4" /> View this week
                  </Link>
                  <a href={downloadUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-[hsl(var(--brand-border))] hover:bg-[hsl(var(--brand-cream-deep))]" data-testid="download-active-pdf-btn">
                    <Download className="w-4 h-4" /> Download PDF
                  </a>
                </div>
              </div>
              <div className="relative hidden md:block bg-canva-beige">
                <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
                <div className="relative h-full flex items-center justify-center p-10">
                  <div className="font-display text-3xl text-center leading-tight text-[hsl(var(--brand-ink))]">
                    Customised
                    <br />
                    <span className="font-serif-italic text-[hsl(var(--brand-sage))]">for {client.name.split(" ")[0]}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-[hsl(var(--brand-border))] rounded-2xl p-10 text-center" data-testid="no-active-plan">
            <div className="font-display text-3xl">No active plan right now.</div>
            <p className="text-[hsl(var(--brand-muted))] mt-2">Amisha will upload your new week's plan shortly. Meanwhile, explore the recipe book.</p>
            <Link to={`${base}/recipes`} className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))]">
              <BookOpen className="w-4 h-4" /> Browse recipes
            </Link>
          </div>
        )}
      </section>

      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-16">
        <Link to={`${base}/recipes`} className="block bg-[hsl(var(--brand-cream-deep))] border border-[hsl(var(--brand-border))] rounded-2xl p-8 card-lift" data-testid="recipes-cta">
          <div className="flex items-center justify-between gap-6">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))]">Recipe book</div>
              <div className="font-display text-3xl mt-2">Every meal, step by step</div>
              <p className="text-[hsl(var(--brand-muted))] mt-2">Search by name, filter by category.</p>
            </div>
            <ArrowRight className="w-6 h-6 text-[hsl(var(--brand-sage))] shrink-0" />
          </div>
        </Link>
      </section>

      {/* Guidelines */}
      {guidelines?.content ? (
        <section className="max-w-5xl mx-auto px-5 md:px-8 mt-10" data-testid="client-guidelines">
          <div className="bg-white border border-[hsl(var(--brand-border))] rounded-2xl p-8 md:p-10 relative">
            <div className="flex items-center gap-2 text-[hsl(var(--brand-sage))]">
              <Scroll className="w-4 h-4" />
              <div className="text-xs tracking-[0.3em] uppercase">A few gentle guidelines</div>
            </div>
            <h3 className="font-display text-3xl mt-2">From Amisha, for the week.</h3>
            <SafeHtml html={guidelines.content} className="nourish-prose mt-5 leading-relaxed text-[hsl(var(--brand-ink))]/90" />
          </div>
        </section>
      ) : null}

      {past.length > 0 ? (
        <section className="max-w-5xl mx-auto px-5 md:px-8 mt-16">
          <h2 className="font-display text-3xl">Earlier plans</h2>
          <div className="mt-5 space-y-3">
            {past.map((p) => (
              <Link key={p.id} to={`${base}/plan/${p.id}`} className="flex items-center justify-between bg-white border border-[hsl(var(--brand-border))] rounded-xl p-5 hover:bg-[hsl(var(--brand-cream))]" data-testid={`past-plan-${p.id}`}>
                <div>
                  <div className="font-medium">{p.title}</div>
                  <div className="text-xs text-[hsl(var(--brand-muted))] flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" /> {p.start_date} — {p.end_date}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-[hsl(var(--brand-muted))]" />
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </ClientLayout>
  );
}
